import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import FullButton from "../components/Buttons/FullButton";
import {
  Container,
  Form,
  FormWrapper,
  Input,
  Message,
  PageWrapper,
  StatusMessage,
  Wrapper,
} from "../style/view.styles";
import { useNavigate, useLocation } from "react-router-dom";
import { makeApiRequest } from "../utils/RequestHandler";
import { AuthContext } from "../contexts/AuthContext";

export default function VerifyOTP() {
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    otp: "",
    email: ""
  });
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from location state (passed from registration)
    if (location.state && location.state.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }

    // Set up countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [location]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { otp } = formData;

    if (!otp) {
      setFormStatus({
        message: "Please enter the OTP code",
        type: "error",
      });
      return;
    }

    setLoading(true);
    setFormStatus({
      message: "",
      type: "success",
    });

    try {
      const appUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${appUrl}/user/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otp: otp,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setFormStatus({
          message: "Verification successful! Logging you in...",
          type: "success",
        });
        login(data.result);
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        setFormStatus({
          message: data.result || "Invalid OTP. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      setFormStatus({
        message: "Something went wrong. Please try again later.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (!formData.email) {
      setFormStatus({
        message: "Email is required for resending OTP",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await makeApiRequest("/user/resend-otp", "POST", {
        email: formData.email,
        api_key: apiKey,
      });

      if (response.success) {
        setFormStatus({
          message: "OTP resent successfully!",
          type: "success",
        });
        setTimeLeft(300); // Reset timer to 5 minutes
      } else {
        setFormStatus({
          message: response.result || "Failed to resend OTP",
          type: "error",
        });
      }
    } catch (error) {
      setFormStatus({
        message: error.message ?? "An error occurred when resending OTP",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Wrapper className="paleBlueGreyBg">
        <Container>
          <PageWrapper className="container flexCenter">
            <FormWrapper>
              <h1 className="extraBold font40 darkGreenColor">Verify OTP</h1>
              <p className="font16" style={{ textAlign: "center", margin: "20px 0" }}>
                Please enter the verification code sent to your email
              </p>
              <Form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  name="otp"
                  placeholder="Enter OTP"
                  value={formData.otp}
                  onChange={handleInputChange}
                  required
                />
                <FullButton title={loading ? "Verifying..." : "Verify OTP"} />
                <TimerSection>
                  {timeLeft > 0 ? (
                    <div>Code expires in: <span className="bold">{formatTime(timeLeft)}</span></div>
                  ) : (
                    <div>OTP has expired</div>
                  )}
                  <ResendButton 
                    onClick={resendOTP} 
                    disabled={timeLeft > 0 || loading}
                    type="button"
                  >
                    Resend OTP
                  </ResendButton>
                </TimerSection>
              </Form>
              {formStatus.message && (
                <StatusMessage type={formStatus.type}>
                  {formStatus.message}
                </StatusMessage>
              )}
              <Message className="font13">
                Back to <a href="/login">Login</a>
              </Message>
            </FormWrapper>
          </PageWrapper>
        </Container>
      </Wrapper>
    </>
  );
}

const TimerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px 0;
  font-size: 14px;
  gap: 10px;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.disabled ? "#aaa" : "#49cb86"};
  text-decoration: underline;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  font-size: 14px;
  padding: 5px;
  
  &:hover {
    color: ${props => props.disabled ? "#aaa" : "#3aa66a"};
  }
`;